import { apiClient } from "@/lib/apiClient";
import type {
  EducationDetailDto,
  ExamAttemptDto,
  CertificateDto,
  EnrollmentDto,
  EducationCommentDto,
  EducationCommentsResponseDto,
} from "../types/client-education-detail.types";
import type { EducationFollowUpAction } from "../utils/follow-up-url";

/**
 * Backend envelope: { success, status, data: {...}, message }.
 * apiClient envelope'u otomatik açmıyor — burada elle çıkarıyoruz.
 * Aksi halde tüm alanlar (startDate, name, vb.) undefined gelir ve sayfa
 * yanlış erken-dönüş ekranlarına düşer.
 */
function unwrap<T>(payload: unknown): T {
  const env = payload as { data?: T } | T | null | undefined;
  if (env && typeof env === "object" && "data" in (env as object)) {
    const inner = (env as { data?: T }).data;
    if (inner !== undefined) return inner as T;
  }
  return payload as T;
}

export const clientEducationDetailApi = {
  getDetail: async (educationId: string): Promise<EducationDetailDto> => {
    const res = await apiClient.get<EducationDetailDto>(
      `/api/client/educations/${educationId}`
    );
    return unwrap<EducationDetailDto>(res.data);
  },

  getMyEnrollment: async (educationId: string): Promise<EnrollmentDto | null> => {
    try {
      const res = await apiClient.get<EnrollmentDto>(
        `/api/client/educations/${educationId}/enrollments/me`
      );
      return unwrap<EnrollmentDto | null>(res.data) ?? null;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw err;
    }
  },

  getMyExamAttempts: async (educationId: string): Promise<ExamAttemptDto[]> => {
    try {
      const res = await apiClient.get<ExamAttemptDto[] | { items?: ExamAttemptDto[] }>(
        `/api/client/educations/${educationId}/exam/me`
      );
      const data = unwrap<ExamAttemptDto[] | { items?: ExamAttemptDto[] }>(res.data);
      if (Array.isArray(data)) return data;
      return data?.items ?? [];
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return [];
      throw err;
    }
  },

  getMyCertificate: async (
    educationId: string,
    enrollmentId: string
  ): Promise<CertificateDto | null> => {
    try {
      const res = await apiClient.get<CertificateDto>(
        `/api/client/educations/${educationId}/enrollments/${enrollmentId}/certificate`
      );
      return unwrap<CertificateDto | null>(res.data) ?? null;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw err;
    }
  },

  getComments: async (
    educationId: string,
    limit = 50,
  ): Promise<EducationCommentsResponseDto> => {
    const res = await apiClient.get<EducationCommentsResponseDto>(
      `/api/client/educations/${educationId}/comments`,
      {
        params: { limit },
        skipErrorToast: true,
      },
    );
    return unwrap<EducationCommentsResponseDto>(res.data);
  },

  createComment: async (
    educationId: string,
    content: string,
  ): Promise<EducationCommentDto> => {
    const res = await apiClient.post<EducationCommentDto>(
      `/api/client/educations/${educationId}/comments`,
      { content },
    );
    return unwrap<EducationCommentDto>(res.data);
  },

  completeObjective: async (
    enrollmentId: string,
    objectiveId: string,
    body?: { watchedSeconds?: number; lastPositionSeconds?: number; progressPercent?: number }
  ): Promise<EducationFollowUpAction | null> => {
    const { data } = await apiClient.post<{
      data?: EducationFollowUpAction | null;
      followUpAction?: EducationFollowUpAction | null;
    } | EducationFollowUpAction | null>(
      `/api/client/education-enrollments/${enrollmentId}/objectives/${objectiveId}/complete`,
      body ?? {}
    );
    // Olası response şekilleri:
    //   - null                                            → henüz tamamlanmadı veya sertifika üretiliyor
    //   - { displayType, type, ... }                     → doğrudan action objesi
    //   - { data: { ...action } }                         → envelope içinde
    //   - { followUpAction: { ...action } }               → named field
    if (data == null) return null;
    if (typeof data === "object" && "displayType" in data) {
      return data as EducationFollowUpAction;
    }
    const envelope = data as {
      data?: EducationFollowUpAction | null;
      followUpAction?: EducationFollowUpAction | null;
    };
    return envelope.data ?? envelope.followUpAction ?? null;
  },

  updateObjectiveProgress: async (
    _enrollmentId: string,
    objectiveId: string,
    body: { watchedSeconds: number; lastPositionSeconds: number; progressPercent: number; watchedSecondsDelta?: number }
  ): Promise<void> => {
    await apiClient.post(
      `/api/client/objectives/${objectiveId}/video/watch`,
      {
        watchedSecondsDelta: body.watchedSecondsDelta ?? body.watchedSeconds,
        lastPositionSeconds: body.lastPositionSeconds,
      }
    );
  },

  cancelEnrollment: async (enrollmentId: string): Promise<void> => {
    await apiClient.post(
      `/api/client/education-enrollments/${enrollmentId}/cancel`
    );
  },

  /**
   * Sınıf (virtual classroom) katılımcı listesini döner.
   * Kursiyer kendi sınıfındaki diğer kişileri görebilsin diye kullanılır.
   * Backend: GET /virtual-classrooms/:id/participants — EDUCATIONS.VIEW yetkisi gerekir.
   */
  getClassroomParticipants: async (
    classroomId: string,
  ): Promise<
    Array<{
      id: string;
      userID: string;
      firstName?: string;
      lastName?: string;
      title?: string | null;
      department?: { id: string; name: string } | null;
    }>
  > => {
    try {
      const res = await apiClient.get(
        `/api/client/virtual-classrooms/${classroomId}/participants`,
      );
      const data = unwrap<{ items?: Array<unknown> } | Array<unknown>>(res.data);
      const list = Array.isArray(data) ? data : (data?.items ?? []);
      return (list as Array<Record<string, unknown>>).map((p) => {
        const u = (p.user ?? p) as Record<string, unknown>;
        return {
          id: String(p.id ?? u.id ?? ""),
          userID: String(p.userID ?? u.id ?? ""),
          firstName: (u.firstName as string) ?? undefined,
          lastName: (u.lastName as string) ?? undefined,
          title: (u.title as string) ?? null,
          department:
            (u.department as { id: string; name: string } | null) ?? null,
        };
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404 || status === 403) return [];
      throw err;
    }
  },
};
